const GEO_RESTRICTION_STYLE_ID = "geo-restriction-styles";
let geoRestrictionObserver = null;

function getRestrictedSelectors() {
  return [
    "#uk-subscribe",
    ".price",
    ".main-product__buy-meta",
    ".header__navigation__icons__cart",
    ".header__navigation__icons__user",
    ".boost-pfs-filter-product-item-price",
    ".ready-to-ship__card__buying",
    ".ready-to-ship__card__price",
    ".collection-page__collection__price",
    ".collections-grid__item__copy__price",
    ".product-groups__item__copy__price",
    ".gallery-rail__form__actions-wrapper",
    ".bespoke-builder__form__actions-wrapper",
  ];
}

function applyGeoRestrictionStyles() {
  if (document.getElementById(GEO_RESTRICTION_STYLE_ID)) {
    return;
  }

  const style = document.createElement("style");
  style.id = GEO_RESTRICTION_STYLE_ID;

  const cssSelectors = getRestrictedSelectors()
    .map((selector) => `body.geo-restricted ${selector}`)
    .join(",\n");

  style.textContent = `${cssSelectors} { display: none !important; }`;
  document.head.appendChild(style);
}

function startGeoRestrictionObserver() {
  if (geoRestrictionObserver) {
    return;
  }

  let rafId = null;
  geoRestrictionObserver = new MutationObserver((mutations) => {
    const hasAddedNodes = mutations.some(
      (mutation) => mutation.addedNodes.length,
    );

    if (!hasAddedNodes || rafId) {
      return;
    }

    rafId = requestAnimationFrame(() => {
      rafId = null;
      amendElements();
    });
  });

  geoRestrictionObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function enableGeoRestrictions() {
  const body = document.body;
  body.classList.add("geo-restricted");
  applyGeoRestrictionStyles();
  amendElements();
  applyRedirects();
  startGeoRestrictionObserver();
  document.dispatchEvent(new CustomEvent("geo:restricted"));
}

/**
 * If the current path is "/cart", the user is redirected to the homepage ("/").
 * @function
 * @returns {void}
 */
function applyRedirects() {
  const currentPath = window.location.pathname;
  if (currentPath === "/cart") {
    window.location.href = "/";
  }
}

/**
 * Replace generic button text from "Shop now" or "Order now" to "Browse Now".
 * @function
 * @returns {void}
 */
function updateButtonText() {
  document.querySelectorAll(".generic-button > *").forEach((element) => {
    if (
      element.textContent === "Shop now" ||
      element.textContent === "Order now"
    ) {
      element.textContent = "Browse Now";
    }
  });
}

/**
 * Alter specified selectors from the DOM, either by removing or replacing them.
 * @function
 * @returns {void}
 */
function amendElements() {
  // Remove the following elements from the DOM
  const selectorsToRemove = getRestrictedSelectors();

  selectorsToRemove.forEach((selector) => {
    const elements = document.querySelectorAll(selector);
    if (elements) {
      elements.forEach((element) => {
        element.remove();
      });
    }
  });

  updateButtonText();
}

/**
 * Alter specified selectors from the DOM for Canada, keeping .price and .main-product__buy-meta visible.
 * @function
 * @returns {void}
 */
function amendElementsForCanada() {
  // Remove the following elements from the DOM (excluding .price and .main-product__buy-meta)
  const selectorsToRemove = [
    "#uk-subscribe",
    ".header__navigation__icons__cart",
    ".header__navigation__icons__user",
    ".ready-to-ship__card__buying",
    ".main-product__buy-meta__buttons",
    ".main-product__buy-meta__quantity",
  ];

  selectorsToRemove.forEach((selector) => {
    const elements = document.querySelectorAll(selector);
    if (elements) {
      elements.forEach((element) => {
        element.remove();
      });
    }
  });

  updateButtonText();
}

// Countries ipapi.co reports with continent_code "EU", used to synthesise a
// continent when falling back to Shopify's country-only detection endpoint.
const EUROPEAN_COUNTRY_CODES = [
  "AD",
  "AL",
  "AT",
  "AX",
  "BA",
  "BE",
  "BG",
  "BY",
  "CH",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FO",
  "FR",
  "GB",
  "GG",
  "GI",
  "GR",
  "HR",
  "HU",
  "IE",
  "IM",
  "IS",
  "IT",
  "JE",
  "LI",
  "LT",
  "LU",
  "LV",
  "MC",
  "MD",
  "ME",
  "MK",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "RS",
  "RU",
  "SE",
  "SI",
  "SJ",
  "SK",
  "SM",
  "UA",
  "VA",
  "XK",
];

/**
 * Checks that an IPAPI payload is a successful lookup rather than an error
 * response (e.g. rate-limited), which has no country_code.
 * @param {object} data - Parsed IPAPI response.
 * @returns {boolean}
 */
function isValidCountryData(data) {
  return Boolean(
    data && !data.error && data.country_code && data.continent_code,
  );
}

/**
 * Checks if a given country code is in the list of restricted countries.
 * @param {object} countryData - An object containing the user's country code amongst other geolocation data.
 * @returns {boolean} Whether the data was valid and restrictions were evaluated.
 */
function checkCountry(countryData) {
  let data = null;
  try {
    data = JSON.parse(countryData);
  } catch (error) {
    data = null;
  }

  if (!isValidCountryData(data)) {
    return false;
  }

  const { country_code: countryCode, continent_code: continentCode } = data;

  const acceptedCountries = [
    "AE",
    "AR",
    "BH",
    "BO",
    "BR",
    "CL",
    "CO",
    "EC",
    "FK",
    "GB",
    "GF",
    "GY",
    "IL",
    "IN",
    "JP",
    "KR",
    "MX",
    "MY",
    "PE",
    "PH",
    "PY",
    "QA",
    "RE",
    "SA",
    "SR",
    "TH",
    "TW",
    "UY",
    "VE",
  ];

  // Add the country code and continent as a class to the body element
  const body = document.querySelector("body");
  body.classList.add(
    "geo",
    `geo-country-${countryCode.toLowerCase()}`,
    `geo-continent-${continentCode.toLowerCase()}`,
  );

  // Are we on the USA shop?
  const isUsaShop = Boolean(window.Shopify.shop.includes("usa"));

  const isRestrictedCountry =
    continentCode !== "EU" &&
    !acceptedCountries.includes(countryCode) &&
    !isUsaShop;

  const isUsaCanadaRestricted = isUsaShop && countryCode === "CA";

  if (isRestrictedCountry || isUsaCanadaRestricted) {
    // Use class + CSS + observer so dynamically-injected content is also restricted.
    enableGeoRestrictions();
  } else if (countryCode === "IT") {
    // If the country is Italy, show the codefiscale input
    // and hide the tax number input
    // @TODO When creating V2 cart, update this to show/hide based on geo-country class instead
    const codefiscaleInput = document.querySelector(".codefiscale-input");
    const taxnumInput = document.querySelector(".taxnum-input");
    if (codefiscaleInput) codefiscaleInput.style.display = "block";
    if (taxnumInput) taxnumInput.style.display = "none";
  }

  document.dispatchEvent(
    new CustomEvent("geo:resolved", {
      detail: {
        countryCode,
        continentCode,
        isUsaShop,
        isRestricted: isRestrictedCountry || isUsaCanadaRestricted,
      },
    }),
  );

  return true;
}

/**
 * Fallback lookup via Shopify's first-party country detection endpoint.
 * ipapi.co is on common privacy/tracker blocklists, so VPN and ad-block
 * users never complete the primary lookup; this endpoint is same-origin
 * and can't be blocked without breaking the site itself. It only returns
 * a country, so the continent is synthesised from EUROPEAN_COUNTRY_CODES.
 * @returns {void}
 */
function shopifyFallbackGeolocation() {
  fetch("/browsing_context_suggestions.json")
    .then((response) => response.json())
    .then((data) => {
      const countryCode =
        data && data.detected_values && data.detected_values.country
          ? data.detected_values.country.handle
          : null;

      if (!countryCode) {
        return;
      }

      const countryData = JSON.stringify({
        country_code: countryCode,
        continent_code: EUROPEAN_COUNTRY_CODES.includes(countryCode)
          ? "EU"
          : "XX",
      });

      sessionStorage.setItem("userCountry", countryData);
      checkCountry(countryData);
    })
    .catch((error) => console.error(error));
}

/**
 * Gets the user's country from sessionStorage or IPAPI and checks if it is in a list of restricted countries.
 * @returns {void}
 */
function geolocation() {
  // 1. check if the users' country is stored in sessionStorage.
  const userCountry = sessionStorage.getItem("userCountry");

  // 2. use the stored country if it's a valid lookup; otherwise discard it
  // (e.g. a cached rate-limit error response) and fetch a fresh one from IPAPI.
  if (userCountry && checkCountry(userCountry)) {
    return;
  }

  sessionStorage.removeItem("userCountry");

  fetch(
    "https://ipapi.co/json/?key=6QPq56VCxQknCDj77QdtdMnOX6gdAiUohGLAJRpCwtJlgwIHb3",
  )
    .then((response) => response.json())
    .then((data) => {
      // Only cache successful lookups — caching an error response would
      // leave restrictions permanently unapplied for the whole session.
      if (!isValidCountryData(data)) {
        console.error("IPAPI lookup returned invalid data", data);
        shopifyFallbackGeolocation();
        return;
      }

      const countryData = JSON.stringify(data);
      sessionStorage.setItem("userCountry", countryData);
      checkCountry(countryData);
    })
    .catch((error) => {
      console.error(error);
      shopifyFallbackGeolocation();
    });
}

// Call the function to start the geolocation process
geolocation();
